/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : CampaignMain.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import { Box, IconButton, Menu, MenuItem, Typography } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import CustomDataGridV2 from "../../components/customComponentsV2/common/datagrid/CustomDataGridV2";
import {
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AddTestCampaignDialog from "./components/AddTestCampaignDialog";
import { useDispatch, useSelector } from "react-redux";
import { HandleApiActions } from "../../redux/actions";
import testCasesActions from "../../redux/actions/testCasesActions";
import { convertUtcToTimeZone } from "../../utils/convertUtcToTimeZone";
import CommonBreadcrumbs from "../../components/customComponentsV2/common/Breadcrumbs/CommonBreadcrumbs";
import CustomSearchbarWithApiV2 from "../../components/customComponentsV2/common/searchBar/CustomSearchbarWithApiV2";
import AddButtonV2 from "../../components/customComponentsV2/common/button/AddButtonV2";
import CustomButtonV2 from "../../components/customComponentsV2/common/button/CustomButtonV2";
function CampaignMain() {
  const dispatch = useDispatch();
  const profileDetails = useSelector((store) => store.profileReducer);
  const [openAddTestCampaignDialog, setOpenAddTestCampaignDialog] =
    useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState("");
  const [tableRowsData, setTableRowsData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [getRawCampaigns, setGetRawCampaigns] = useState([]);
  const [originalCampaigns, setOriginalCampaigns] = useState([]);

  const [openRecreateCampaignDialog, setOpenRecreateCampaignDialog] =
    useState(false);

  const isSubRoute =
    location.pathname.includes("/dashboard/test-cases/campaign/view") ||
    location.pathname.includes("/dashboard/test-cases/campaign/log") ||
    location.pathname.includes("/dashboard/test-cases/campaign/run");

  const getTabLabel = (tab) => {
    const tabLabels = {
      view: "View",
      run: "Run",
      log: "Log",
    };
    return tabLabels[tab] || "";
  };

  const fetchTestCampaigns = useCallback(async () => {
    const response = await dispatch(
      HandleApiActions({
        ...testCasesActions.get_test_campaigns,
        show_toast: false,
      }),
    );

    if (response && !response.error) {
      setTotalCount(response.total_count || 0);
      const campaignsData = response.data || [];
      setGetRawCampaigns(campaignsData);
      setOriginalCampaigns(campaignsData);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchTestCampaigns();
  }, [fetchTestCampaigns]);

  useEffect(() => {
    const tab = new URLSearchParams(location.search).get("tab") || "";
    setCurrentTab(tab);
  }, [location.search]);

  useEffect(() => {
    const formattedData =
      getRawCampaigns?.map((tc, index) => ({
        ...tc,
        id: tc?.campaign_id || (page - 1) * rowsPerPage + index + 1,
        sNo: (page - 1) * rowsPerPage + index + 1,
        created_at: convertUtcToTimeZone(
          tc?.created_at,
          profileDetails?.profile?.profile?.region,
        ),
        updated_at: convertUtcToTimeZone(
          tc?.updated_at,
          profileDetails?.profile?.profile?.region,
        ),
      })) || [];

    setTableRowsData(formattedData);
  }, [
    getRawCampaigns,
    page,
    rowsPerPage,
    profileDetails?.profile?.profile?.region,
  ]);

  const handleSearchData = (data, resp) => {
    if (data === null) {
      setTotalCount(originalCampaigns?.length || 0);
      setGetRawCampaigns(originalCampaigns);
    } else if (resp?.data?.length > 0) {
      setTotalCount(resp.total_count || 0);
      setGetRawCampaigns(resp.data || []);
    } else {
      setTableRowsData([]);
      setTotalCount(0);
    }
  };

  const handleMenuOpen = (event, row) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleViewTestCampaign = () => {
    if (selectedRow) {
      navigate(`view/${selectedRow.campaign_id}?tab=view`);
      handleMenuClose();
    }
  };

  const handleViewTestCampaignLogs = () => {
    if (selectedRow) {
      navigate(`view/${selectedRow.campaign_id}?tab=log`);
      handleMenuClose();
    }
  };

  const handleRunTestCampaign = async () => {
    if (selectedRow) {
      navigate(`view/${selectedRow.campaign_id}?tab=run`);
      handleMenuClose();
    }
  };

  const breadcrumbItems = [{ label: "Test Cases" }, { label: "Campaigns" }];

  const COLUMNS = [
    {
      field: "sNo",
      headerName: "S.No",
      flex: 0.3,
      minWidth: 60,
      filterable: false,
      sortable: false,
    },
    {
      field: "name",
      headerName: "Test Campaign Name",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        const formatStatus = (status) => {
          if (!status) return "-";
          return status
            .split("-")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
        };

        return (
          <Typography
            variant="body2"
            sx={{
              fontSize: 10,
            }}
          >
            {formatStatus(params.row.status)}
          </Typography>
        );
      },
    },
    {
      field: "created_at",
      headerName: "Created At",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "updated_at",
      headerName: "Updated At",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.5,
      minWidth: 100,
      renderCell: (params) => (
        <IconButton
          size="small"
          sx={{ color: "#000000", p: 0.5 }}
          onClick={(e) => handleMenuOpen(e, params.row)}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <Box
      sx={{
        margin: 0,
        padding: 0,
        width: "100%",
        height: "100%",
        overflow: "auto",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#F5F5F5",
      }}
    >
      {isSubRoute ? (
        <Box
          sx={{
            padding: { xs: "8px 8px 0px 8px", md: "12px 12px 0px 12px" },
            flexShrink: 0,
          }}
        >
          <CommonBreadcrumbs
            items={
              currentTab
                ? [
                    { label: "Test Cases" },
                    { label: "Campaign" },
                    { label: getTabLabel(currentTab) },
                  ]
                : [{ label: "Test Cases" }, { label: "Campaign" }]
            }
          />

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              mt: 1,
            }}
          >
            <CustomButtonV2
              size="small"
              variant="contained"
              btnName="Back"
              startIcon={<ArrowBackIcon />}
              handleOnClick={() => navigate("/dashboard/test-cases/campaign")}
            />
          </Box>

          <Outlet />
        </Box>
      ) : (
        <>
          <Box
            sx={{
              padding: { xs: "8px 8px 0px 8px", md: "12px 12px 0px 12px" },
              flexShrink: 0,
            }}
          >
            <CommonBreadcrumbs items={breadcrumbItems} />
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: { xs: "0 16px 12px 16px", md: "0 24px 16px 24px" },
              flexShrink: 0,
              mt: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexShrink: 0,
                gap: 1,
              }}
            >
              
              <Box>
                <CustomSearchbarWithApiV2
                  placeholder="Search Campaign Name"
                  apiAction={testCasesActions.get_test_campaigns}
                  searchKey={"name"}
                  params={{
                    page_number: page,
                    page_size: rowsPerPage,
                  }}
                  getDataOnSearch={handleSearchData}
                />
              </Box>
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Box>
                <AddButtonV2
                  icon={"refresh"}
                  handleOnClick={() => {
                    setOpenRecreateCampaignDialog(true);
                  }}
                />
              </Box>
              <Box>
                <AddButtonV2
                  handleOnClick={() => setOpenAddTestCampaignDialog(true)}
                />
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              height: "78vh",
              width: "100%",
              display: "flex",
              flexDirection: "column",
              padding: { xs: "0 8px", md: "0 16px" },
              mt: -1,
            }}
          >
            <CustomDataGridV2
              rows={tableRowsData}
              columns={COLUMNS}
              isMultiLine={true}
              onRowClick={() => {}}
              showPagination={true}
              page={page}
              rowsPerPage={rowsPerPage}
              handlePageChange={(newPage) => setPage(newPage)}
              handlePageSizeChange={(newSize) => {
                setRowsPerPage(newSize);
                setPage(1);
              }}
              totalItems={totalCount}
            />
          </Box>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            slotProps={{
              paper: {
                sx: {
                  minWidth: 110,
                },
              },
            }}
          >
            <MenuItem onClick={handleViewTestCampaign}>View</MenuItem>
            <MenuItem onClick={handleRunTestCampaign}>Run</MenuItem>
            <MenuItem onClick={handleViewTestCampaignLogs}>Log</MenuItem>
          </Menu>

          <AddTestCampaignDialog
            open={openAddTestCampaignDialog}
            onClose={setOpenAddTestCampaignDialog}
            onSuccess={fetchTestCampaigns}
          />
          <AddTestCampaignDialog
            open={openRecreateCampaignDialog}
            onClose={setOpenRecreateCampaignDialog}
            dialogMode="recreate"
            onSuccess={fetchTestCampaigns}
          />
        </>
      )}
    </Box>
  );
}

export default CampaignMain;



